import { supabase } from "../../config/supabaseClient";

/**
 * Shared trending-click analytics fetch for Trending Content / Products.
 *
 * Uses `get_post_clicks_analyticss_trending_date_created_v2`, then client-side
 * merge by post_id + min_clicks + maxRes.
 *
 * Count semantics: Post Click + Redirect to shop link.
 */

export const TRENDING_ANALYTICS_RPC_BY_POST =
  "get_post_clicks_analyticss_trending_by_post";

export const TRENDING_ANALYTICS_RPC_V2 =
  "get_post_clicks_analyticss_trending_date_created_v2";

export type TrendingAnalyticsRow = {
  post: string;
  count: number;
};

export type TrendingAnalyticsResult = {
  rows: TrendingAnalyticsRow[];
  analyticsRpc: string;
  rawRows: number;
  preAggregated: boolean;
};

function mergeRowsByPostId(
  data: TrendingAnalyticsRow[] | null | undefined
): TrendingAnalyticsRow[] {
  if (!Array.isArray(data) || data.length === 0) return [];

  const merged: Record<
    string,
    { count: number; post: Record<string, unknown> }
  > = {};

  for (const item of data) {
    try {
      const post = JSON.parse(String(item.post || "{}")) as Record<
        string,
        unknown
      >;
      const id = String(post?.post_id || "").trim();
      if (!id) continue;
      const count = Number(item.count) || 0;
      if (!merged[id]) {
        merged[id] = { count, post };
      } else {
        merged[id].count += count;
      }
    } catch {
      // skip bad rows
    }
  }

  return Object.entries(merged)
    .sort((a, b) => b[1].count - a[1].count)
    .map(([id, value]) => ({
      post: JSON.stringify({ ...value.post, post_id: id }),
      count: value.count,
    }));
}

function filterByMinClicks(
  rows: TrendingAnalyticsRow[],
  minClicks: number
): TrendingAnalyticsRow[] {
  const threshold = Math.max(1, Number(minClicks) || 1);
  return rows.filter((row) => Number(row.count) >= threshold);
}

/**
 * Returns `{ post, count }` rows sorted by count DESC, filtered to minClicks.
 * Prefer calling with maxRes sized for the surface (strip vs full carousel).
 */
export async function fetchTrendingClickAnalytics(args: {
  username: string;
  userId: string;
  startDate: string;
  endDate: string;
  minClicks?: number | string;
  maxRes?: number;
}): Promise<TrendingAnalyticsResult> {
  const username = String(args.username || "")
    .trim()
    .toLowerCase();
  const route_name = `/${username}`;
  const userid = args.userId;
  const start_date = args.startDate;
  const end_date = args.endDate;
  const min_clicks = Math.max(1, Number(args.minClicks) || 1);
  const max_res = Math.max(1, Number(args.maxRes) || 5000);

  if (!userid || !username || !start_date || !end_date) {
    return {
      rows: [],
      analyticsRpc: "none",
      rawRows: 0,
      preAggregated: false,
    };
  }

  const { data, error } = await supabase.rpc(TRENDING_ANALYTICS_RPC_V2, {
    route_name,
    userid,
    start_date,
    end_date,
  });

  if (error || !data) {
    return {
      rows: [],
      analyticsRpc: TRENDING_ANALYTICS_RPC_V2,
      rawRows: 0,
      preAggregated: false,
    };
  }

  const merged = filterByMinClicks(
    mergeRowsByPostId(data as TrendingAnalyticsRow[]),
    min_clicks
  ).slice(0, max_res);

  return {
    rows: merged,
    analyticsRpc: TRENDING_ANALYTICS_RPC_V2,
    rawRows: Array.isArray(data) ? data.length : 0,
    preAggregated: false,
  };
}

/** post_id list from analytics rows (already sorted by count DESC). */
export function trendingPostIdsFromAnalyticsRows(
  rows: TrendingAnalyticsRow[]
): string[] {
  const ids: string[] = [];
  for (const row of rows) {
    try {
      const id = String(
        JSON.parse(String(row.post || "{}"))?.post_id || ""
      ).trim();
      if (id) ids.push(id);
    } catch {
      // skip
    }
  }
  return ids;
}
